import React, { useState } from "react";
import { API_BASE_URL } from '../lib/constants';

interface SignUpFormProps {
  error?: string | string[] | null;
  setError?: (err: string | string[] | null) => void;
  setActiveTab?: (tab: 'login' | 'signup') => void;
  setSuccess?: (success: boolean) => void;
  onSuccess?: () => void;
  initialJoinCode?: string;
}

export function SignUpForm({ error, setError, setActiveTab, setSuccess, onSuccess, initialJoinCode }: SignUpFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [joinCode, setJoinCode] = useState(initialJoinCode || "");
  const [isLoading, setIsLoading] = useState(false);
  // Remove local success state, use parent tab switching instead

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== repeatPassword) {
      if (setError) setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    try {
      const requestBody: any = { username: email, email, password };
      if (joinCode.trim()) {
        requestBody.join_code = joinCode.trim().toUpperCase();
      }
      
      const response = await fetch(`${API_BASE_URL}/api/sign-up/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody)
      });
      const data = await response.json();
      if (response.status === 201) {
        // On success, switch to login tab and show success message there
        if (setSuccess) setSuccess(true);
        if (setActiveTab) setActiveTab('login');
        if (setError) setError(null);
        if (onSuccess) onSuccess();
        // Reset form fields only on successful submission
        setEmail("");
        setPassword("");
        setRepeatPassword("");
        // Reset join code only if it wasn't initially provided
        if (!initialJoinCode) {
          setJoinCode("");
        }
      } else {
        let errors: string[] = [];
        if (data && Array.isArray(data.errors)) {
          errors = data.errors.map((e: any) => String(e));
        } else {
          errors = ["Sign-up failed"];
        }
        if (setError) setError(errors);
        // Don't reset any fields on error - let user correct and retry
      }
    } catch (error: any) {
      if (setError) setError(error.message || "An error occurred");
      // Don't reset fields on network/other errors either
    } finally {
      setIsLoading(false);
    }
  };

  // Format error messages: stack each error on its own line
  let errorMessages: string[] = [];
  if (error) {
    if (typeof error === 'string') {
      let parsed = null;
      try {
        // Try to parse as JSON
        parsed = JSON.parse(error);
      } catch {
        // Try to parse Python-style list string: ['msg1', 'msg2']
        if (error.startsWith("[") && error.endsWith("]")) {
          // Remove brackets and split by comma
          const items = error.slice(1, -1).split(/,(?=(?:[^']*'[^']*')*[^']*$)/);
          errorMessages = items.map(item => item.replace(/^\s*'|'\s*$/g, "").trim().replace(/^"|"$/g, ""));
        } else {
          errorMessages = [error];
        }
      }
      if (parsed !== null) {
        if (Array.isArray(parsed)) {
          errorMessages = parsed.map(e => String(e));
        } else if (typeof parsed === 'object' && parsed !== null) {
          errorMessages = Object.values(parsed).flat().map(e => String(e));
        } else {
          errorMessages = [String(parsed)];
        }
      }
    } else if (Array.isArray(error)) {
      errorMessages = error.map((e: any) => String(e));
    } else if (typeof error === 'object' && error !== null) {
      errorMessages = Object.values(error as any).flat().map((e: any) => String(e));
    }
  }


  return (
    <form onSubmit={handleSignUp} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        style={{ padding: 10, borderRadius: 6, border: "1px solid #ddd" }}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
        style={{ padding: 10, borderRadius: 6, border: "1px solid #ddd" }}
      />
      <input
        type="password"
        placeholder="Confirm Password"
        value={repeatPassword}
        onChange={e => setRepeatPassword(e.target.value)}
        required
        style={{ padding: 10, borderRadius: 6, border: "1px solid #ddd" }}
      />
      <input
        type="text"
        placeholder="Family Join Code (Optional)"
        value={joinCode}
        onChange={e => setJoinCode(e.target.value.toUpperCase())}
        maxLength={6}
        style={{ 
          padding: 10, 
          borderRadius: 6, 
          border: "1px solid #ddd",
          fontFamily: 'monospace',
          fontSize: 16,
          letterSpacing: 1
        }}
      />
      {joinCode.trim() && (
        <div style={{ fontSize: 12, color: '#666', marginTop: -8 }}>
          You'll be added to the family after the admin approves your request.
        </div>
      )}
      {errorMessages.length > 0 && (
        <div style={{ color: "red", marginBottom: 8 }}>
          {errorMessages.map((msg, idx) => (
            <div key={idx}>{msg}</div>
          ))}
        </div>
      )}
      <button type="submit" disabled={isLoading} style={{ padding: 10, borderRadius: 6, background: "#2d6cdf", color: "#fff", fontWeight: 600, border: "none" }}>
        {isLoading ? "Signing up..." : "Sign Up"}
      </button>
    </form>
  );
}
